/**
 * Step1Page - Página principal del Step 1 (Normaliza)
 * 
 * Dos estados visuales:
 * 1. Sin archivo: Solo FileDropzone centrado (sin título redundante)
 * 2. Con archivo: InfoBanner, filtros, tabla
 * 
 * @source P1_Normaliza_v7_fixed.html
 * @version 2.1.0 - Estado vacío simplificado, sin duplicación de título
 */

import React, { useEffect, useMemo, useState, type FC, type CSSProperties } from 'react';
import { toast } from 'sonner';
import { typography, spacing } from '@/design-system/v2';

// Shell components
import { InfoBanner, BannerStrong } from '../shell/InfoBanner';
import { FooterBar } from '../shell/FooterBar';

// Shared components
import { SearchBox, FilterPills, type FilterOption as FilterType } from '@/design-system/v2';
import { Pagination } from '../shared/Pagination';

// Step1 specific
import { FileDropzone } from './FileDropzone';
import {
  DocumentStatusModule, type ProcesoProgress, type PastillaIncidencias, type GrupoIncidencias,
} from './DocumentStatusModule';
import type { ProcessedInfrastructure } from '@/types/processing';
import {
  detectarIncidenciasDocumento, medirContencionDelDocumento, CODIGOS_INCIDENCIA, FICHA_INCIDENCIA,
  type ContencionDocumento, type InformeIncidenciasDocumento,
  type ClaseIncidencia, type IncidenciaDocumento,
} from '@/services/analysis/documentIncidents';
import {
  TEXTO_INCIDENCIA, plural, mayuscula, distancia, enumerar, textoDocumentoLejos,
} from '@/services/analysis/textosIncidencias';
import type {
  DetalleIncidencias, ApartadoIncidencias, FilaConIncidencias, EtiquetaClase, ResultadoDescargaInforme,
} from './PanelIncidencias';
import { type ViaBreakdown, emptyViaBreakdown } from '@/utils/procedenciaVia';
import { ApiHealthBanner } from '../../shared/ApiHealthBanner';

import { NormalizationTable, type NormalizationRow } from './NormalizationTable';
import { type TypologyFilter } from './TypeDropdown';
// P1UNO: `ProgressBar` ya no se monta en P1 — el proceso vive en DocumentStatusModule.

// ============================================================================
// TYPES
// ============================================================================

export interface DocumentContext {
  fileName: string;
  municipio: string;
  provincia: string;
  ine: string;
  /**
   * INFDOC-3a (DOP-8 a de INFDOC-0): `sha256` del documento tal como se cargó. Sin
   * él cuando no se ha calculado aún o el documento viene de «Recuperar» (el
   * fichero recreado no conserva el contenido). Consumidor: los informes de
   * INFDOC-3b y 3c; hasta entonces, sin uso.
   */
  huella?: string;
}

/**
 * P1-S2 (escenario 3): diagnóstico del caso «documento válido con
 * 0 infraestructuras reconocidas». Se construye con lo que el extractor
 * ya expone (formato + municipio/INE detectados del nombre del archivo);
 * el preview de filas crudas queda diferido a la sub-rebanada S2-b
 * (exige ampliar el contrato del extractor).
 */
export interface ZeroInfraDiagnostic {
  fileName: string;
  fileType: string;
  municipio?: string;
  provincia?: string;
  ine?: string;
}

/**
 * P1-S3 (escenario 4): candidato para el confirmador de municipio.
 * Estructuralmente idéntico a CandidatoMunicipio del catálogo INE
 * (src/lib/codigosINEUnificado.ts) — se duplica aquí para mantener
 * el componente presentacional desacoplado de la librería.
 */
export interface MunicipioCandidato {
  codigo: string;
  nombre: string;
  provincia: string;
}

/**
 * P1-S3 (escenario 4): diagnóstico «municipio no detectado o ambiguo».
 * El INE condiciona la cascada de geolocalización y la validación, por lo
 * que el procesamiento queda BLOQUEADO hasta que un usuario con permiso
 * confirme el municipio. El catálogo INE maestro es inmutable: la
 * confirmación se registra como metadato del trabajo, nunca en el catálogo.
 */
export interface MunicipioPendienteDiagnostic {
  fileName: string;
  /** Nombre detectado en el documento que no resolvió a INE (si lo hubo) */
  municipioDetectado?: string;
  /** Homónimos/próximos del catálogo de los 785, con provincia */
  candidatos: MunicipioCandidato[];
}

export interface Step1PageProps {
  /** Contexto del documento (null = sin archivo cargado) */
  documentContext: DocumentContext | null;
  /** Datos de infraestructuras */
  data: NormalizationRow[];
  /** Métricas: geolocalizados / total */
  geolocatedCount: number;
  totalCount: number;
  /** Modo demo */
  isDemo?: boolean;
  /** Estado de procesamiento de archivo */
  isProcessing?: boolean;
  /** Mensaje durante procesamiento */
  processingStatus?: string;
  /** Error de procesamiento */
  processingError?: string | null;
  /** P1-S2 (esc. 3): diagnóstico de documento válido con 0 infraestructuras */
  zeroInfraDiagnostic?: ZeroInfraDiagnostic | null;
  /** P1-S3 (esc. 4): diagnóstico de municipio sin confirmar (gate bloqueante) */
  municipioPendiente?: MunicipioPendienteDiagnostic | null;
  /** P1-S3: si el usuario tiene permiso para confirmar municipio (editor+) */
  canConfirmMunicipio?: boolean;
  /** P1-S3: búsqueda sobre el catálogo de los 785 (inyectada, testable) */
  searchMunicipios?: (query: string) => MunicipioCandidato[];
  /** P1-S3: callback al confirmar municipio (recibe el código INE elegido) */
  onConfirmMunicipio?: (codigo: string) => void;
  /** Estado de normalización en curso */
  isNormalizing?: boolean;
  /** Progreso de normalización */
  normalizationProgress?: ProcesoProgress;
  /** BUG-4: Estado de geocodificación automática en curso */
  isGeocoding?: boolean;
  /** BUG-4: Progreso de geocodificación (misma forma; trae además porVia y porCalidad) */
  geoProgress?: ProcesoProgress;
  /** Callback al seleccionar archivo */
  onFileSelect?: (file: File) => void;
  /** Callback al resetear archivo */
  onFileReset?: () => void;
  /** Callback al hacer clic en pin */
  onPinClick?: (row: NormalizationRow) => void;
  /** Callback al copiar coordenadas */
  onCopyCoords?: (row: NormalizationRow) => void;
  /** Callback al continuar al paso 2 (Valida) */
  onContinue?: () => void;
  /**
   * INFDOC-2a: filas finales de P1 (`ProcessedInfrastructure`, con las
   * correcciones de P2), para el detector de incidencias. `data` es la
   * proyección de la tabla y no lleva lo que el detector necesita. Sin esta
   * prop no hay pastilla (DOP-2 a).
   */
  filasFinales?: readonly ProcessedInfrastructure[];
  /** INFDOC-2a: INE real del documento; nunca `documentContext.ine`, que pone «00000» si falta */
  ineDocumento?: string;
  /** INFDOC-2a: P1 completado (fin de la geolocalización); la fase «terminado» del panel no basta (P1PROV) */
  p1Terminado?: boolean;
}

// ============================================================================
// INFDOC-2a · INCIDENCIAS DEL DOCUMENTO (pastilla de P1)
// ============================================================================

// INFDOC-3a: los textos viven en `textosIncidencias.ts`, comunes con los informes.

/**
 * Resumen de la pastilla (DOP-1 b: N = filas con alguna incidencia; DOP-3 a:
 * una línea por tipo; DOP-5: aviso de filas sin comprobar el límite;
 * propuesta B: un apartado por destinatario). Exportado para las pruebas.
 */
export function resumirIncidencias(informe: InformeIncidenciasDocumento): PastillaIncidencias {
  const ayto = new Set<string>();
  const grea = new Set<string>();
  const puntos = new Set<string>();
  for (const inc of informe.incidencias) {
    if (inc.filaId === null) continue;
    (FICHA_INCIDENCIA[inc.codigo].destino === 'ayuntamiento' ? ayto : grea).add(inc.filaId);
    if (inc.codigo === 'coordenada_compartida') puntos.add([inc.filaId, ...(inc.conFilas ?? [])].sort().join('|'));
  }
  const soloGrea = [...grea].filter(id => !ayto.has(id)).length;
  const lineasDe = (destino: 'ayuntamiento' | 'grea'): GrupoIncidencias['lineas'] => CODIGOS_INCIDENCIA
    .filter(c => FICHA_INCIDENCIA[c].destino === destino && informe.porCodigo[c] > 0)
    .map(c => ({
      n: informe.porCodigo[c],
      texto: TEXTO_INCIDENCIA[c](informe.porCodigo[c]),
      ...(c === 'coordenada_compartida' ? { apunte: `${puntos.size} ${plural(puntos.size, 'punto', 'puntos')}` } : {}),
    }));
  const grupos: GrupoIncidencias[] = [
    { titulo: 'Para el ayuntamiento', filas: ayto.size, lineas: lineasDe('ayuntamiento') },
    // `documento_sin_municipio` no es de fila: su línea va al GREA aunque no sume filas
    { titulo: 'Solo para el GREA', filas: soloGrea, lineas: lineasDe('grea') },
  ].filter(g => g.lineas.length > 0);
  const aviso = avisoSinValidar(informe.filasSinValidarLimite);
  return { estado: 'lista', filas: informe.filasConIncidencia, grupos, ...(aviso ? { aviso } : {}) };
}

/** DOP-5 de INFDOC-2: el mismo aviso en el resumen y en la ventana, solo si hay alguna */
function avisoSinValidar(sv: number): string | undefined {
  return sv > 0
    ? `${sv} ${plural(sv, 'fila', 'filas')} sin comprobar si ${plural(sv, 'cae', 'caen')} dentro del término: `
      + 'no se pudo cargar el límite municipal.'
    : undefined;
}

// ─── INFDOC-2b · detalle por fila para la ventana ───────────────────────────

const ETIQUETA_CLASE: Record<ClaseIncidencia, EtiquetaClase> = {
  comprobada: 'comprobada',
  por_confirmar: 'por confirmar',
  solo_grea: 'solo GREA',
};

/**
 * Ventana de INFDOC-2b: una fila con todas sus incidencias (DOP-4 a de INFDOC-2),
 * en el apartado del ayuntamiento si tiene alguna para él, y si no en el del
 * GREA; dentro, por posición (DOP-2 b). Mismos conjuntos que `resumirIncidencias`,
 * así que las cifras de los apartados son las del resumen. Datos extra: distancia
 * y filas con las que comparte (DOP-4 b). INFDOC-3a: «documento lejos» con la frase
 * de `textoDocumentoLejos` y, dentro de la fila, el ayuntamiento antes que el GREA.
 * Exportado para las pruebas.
 */
export function detallarIncidencias(
  informe: InformeIncidenciasDocumento,
  filas: readonly ProcessedInfrastructure[],
): DetalleIncidencias {
  const numero = new Map(filas.map((f, i) => [f.id, i + 1]));
  const porFila = new Map<string, { indice: number; lista: IncidenciaDocumento[] }>();
  const notasGrea: string[] = [];
  for (const inc of informe.incidencias) {
    if (inc.filaId === null || inc.indice === null) {
      notasGrea.push(mayuscula(TEXTO_INCIDENCIA[inc.codigo](1)) + '.');
      continue;
    }
    const e = porFila.get(inc.filaId) ?? { indice: inc.indice, lista: [] };
    e.lista.push(inc);
    porFila.set(inc.filaId, e);
  }
  const apunteDe = (inc: IncidenciaDocumento): string | undefined => {
    const otras = (inc.conFilas ?? []).map(id => numero.get(id)).filter((n): n is number => n !== undefined)
      .sort((a, b) => a - b);
    switch (inc.codigo) {
      case 'coordenada_compartida': return `${plural(otras.length, 'fila', 'filas')} ${enumerar(otras)}`;
      case 'fila_repetida': return `repite la fila ${enumerar(otras)}`;
      case 'fuera_del_termino_mas_de_500_m':
      case 'fuera_del_termino_hasta_500_m':
        return inc.distanciaMetros !== undefined ? `a ${distancia(inc.distanciaMetros)} del límite` : undefined;
      // INFDOC-3a (DOP-3a-2 a): la distancia va dentro de la frase (`textoDe`)
      default: return undefined;
    }
  };
  const textoDe = (inc: IncidenciaDocumento): string =>
    (inc.codigo === 'documento_lejos_de_fuentes_que_coinciden' && inc.distanciaMetros !== undefined && inc.fuentes
      ? textoDocumentoLejos(inc.distanciaMetros, inc.fuentes)
      : mayuscula(TEXTO_INCIDENCIA[inc.codigo](1)));
  // INFDOC-3a (DOP-3a-1 a): dentro de la fila, primero las del ayuntamiento; `sort` es estable
  const rango = (inc: IncidenciaDocumento) => (FICHA_INCIDENCIA[inc.codigo].destino === 'ayuntamiento' ? 0 : 1);
  const ayto: FilaConIncidencias[] = [];
  const grea: FilaConIncidencias[] = [];
  for (const { indice, lista } of [...porFila.values()].sort((a, b) => a.indice - b.indice)) {
    const deAyto = lista.some(inc => FICHA_INCIDENCIA[inc.codigo].destino === 'ayuntamiento');
    (deAyto ? ayto : grea).push({
      fila: indice + 1,
      elemento: filas[indice]?.nombre ?? '',
      incidencias: [...lista].sort((a, b) => rango(a) - rango(b)).map(inc => {
        const apunte = apunteDe(inc);
        const ficha = FICHA_INCIDENCIA[inc.codigo];
        return {
          texto: textoDe(inc),
          ...(apunte ? { apunte } : {}),
          // DOP-2 b: en el apartado del ayuntamiento, la del GREA se marca como tal
          clase: deAyto && ficha.destino === 'grea' ? 'solo GREA' : ETIQUETA_CLASE[ficha.clase],
        };
      }),
    });
  }
  const apartados: ApartadoIncidencias[] = [
    { titulo: 'Para el ayuntamiento', filas: ayto },
    { titulo: 'Solo para el GREA', filas: grea, ...(notasGrea.length ? { notas: notasGrea } : {}) },
  ].filter(a => a.filas.length > 0 || (a.notas?.length ?? 0) > 0);
  const aviso = avisoSinValidar(informe.filasSinValidarLimite);
  return { apartados, ...(aviso ? { aviso } : {}) };
}

/**
 * Comprobación del límite en curso o hecha, por documento: la coordenada del
 * documento no cambia con las correcciones de P2 (AUDIT_INFDOC2 §4.4), así
 * que se mide una vez y la reutilizan los renders y los regresos a P1. Solo
 * se guarda la del último documento.
 */
let ultimaContencion: { clave: string; promesa: Promise<Map<string, ContencionDocumento>> } | null = null;

function contencionDe(clave: string, filas: readonly ProcessedInfrastructure[], ine: string) {
  if (ultimaContencion?.clave !== clave) {
    ultimaContencion = { clave, promesa: medirContencionDelDocumento(filas, ine) };
  }
  return ultimaContencion.promesa;
}

/** INFDOC-3b2: lo que el PDF del ayuntamiento necesita del documento cargado */
export interface DocumentoInforme {
  fileName: string;
  municipio: string;
  provincia: string;
  /** INE real (nunca el «00000» de `documentContext.ine`); vacío si no se conoce */
  ine: string;
  /** Sin ella si el documento viene de «Recuperar»: el PDF lo dice */
  huella?: string;
}

/**
 * INFDOC-3b2: prepara el PDF del ayuntamiento (`InformeAyuntamientoPDF`, 3b1),
 * con las ortofotos PNOA de sus puntos, y lo descarga como P4
 * (`ExportService.downloadBlob`). Nombre (DOP-2 a): prefijo + patrón del ZIP de
 * P4, con su misma regla de saneado y su hora local. Carga perezosa: jsPDF y el
 * generador no entran en P1 hasta que se pulsa el botón.
 */
export async function descargarInformeAyuntamiento(
  informe: InformeIncidenciasDocumento,
  filas: readonly ProcessedInfrastructure[],
  documento: DocumentoInforme,
  fecha: Date = new Date(),
): Promise<ResultadoDescargaInforme> {
  const [{ ExportService }, pdf] = await Promise.all([
    import('@/services/export/ExportService'),
    import('@/services/export/InformeAyuntamientoPDF'),
  ]);
  const datos = {
    informe,
    filas,
    documento: {
      fileName: documento.fileName,
      municipio: documento.municipio,
      provincia: documento.provincia,
      ...(documento.huella ? { huella: documento.huella } : {}),
    },
    version: __APP_VERSION__,
    fecha,
  };
  // Los mismos puntos que dibujará el PDF, sin repetir (la clave del recorte)
  const puntos = [...new Map(pdf.prepararInformeAyuntamiento(datos).puntos
    .flatMap(p => (p.punto ? [[pdf.claveDePunto(p.punto), p.punto] as const] : []))).values()];
  const recortes = await pdf.pedirRecortesPNOA(puntos);
  const blob = await pdf.generarInformeAyuntamientoPDF({ ...datos, recortes });
  const nombre = `Informe_ayuntamiento_${ExportService.buildLoteZipFilename(documento.municipio, documento.ine, fecha)
    .replace(/\.zip$/, '.pdf')}`;
  ExportService.downloadBlob(blob, nombre);
  return { puntos: puntos.length, sinOrtofoto: puntos.length - recortes.size };
}

/**
 * INFDOC-3c2: prepara el CSV de incidencias del GREA (`IncidenciasGreaCSV`, 3c1)
 * y lo descarga como P4 (`ExportService.downloadBlob`, `text/csv;charset=utf-8`),
 * con el texto y el nombre que da el generador, sin tocarlos (DOP-3 b y DOP-5 a
 * de 3c1: sin BOM). Carga perezosa, como el PDF (DOP-4 a): el generador no entra
 * en P1 hasta que se pulsa el botón.
 */
export async function descargarCsvIncidenciasGrea(
  informe: InformeIncidenciasDocumento,
  filas: readonly ProcessedInfrastructure[],
  documento: Pick<DocumentoInforme, 'municipio' | 'ine'>,
  fecha: Date = new Date(),
): Promise<void> {
  const [{ ExportService }, csv] = await Promise.all([
    import('@/services/export/ExportService'),
    import('@/services/export/IncidenciasGreaCSV'),
  ]);
  const blob = new Blob([csv.generarCsvIncidenciasGrea(informe, filas)], { type: 'text/csv;charset=utf-8' });
  ExportService.downloadBlob(blob, csv.nombreCsvIncidenciasGrea(documento.municipio, documento.ine, fecha));
}

/**
 * INFDOC-2a (DOP-2 a): pastilla de incidencias de P1. Mudo (`undefined`) sin
 * `filasFinales`; «…» hasta que P1 termina y mientras se comprueba el límite;
 * después, el detector síncrono sobre las filas finales, que se rehace al
 * instante si cambian (correcciones de P2). INFDOC-3b2: con `documento`, la
 * ventana trae la orden de descarga del PDF del ayuntamiento; INFDOC-3c2, y la
 * del CSV de incidencias del GREA.
 */
export function useIncidenciasDocumento(
  filas: readonly ProcessedInfrastructure[] | undefined,
  ine: string | undefined,
  terminado: boolean,
  documento?: Pick<DocumentContext, 'fileName' | 'municipio' | 'provincia' | 'huella'> | null,
): PastillaIncidencias | undefined {
  const ineUtil = ine && /^\d{5}$/.test(ine) && ine !== '00000' ? ine : undefined;
  const clave = useMemo(
    () => (filas && terminado && ineUtil ? `${ineUtil}|${filas.map(f => f.id).join(',')}` : ''),
    [filas, terminado, ineUtil],
  );
  const [medida, setMedida] = useState<{ clave: string; contencion: Map<string, ContencionDocumento> } | null>(null);
  useEffect(() => {
    if (!clave || !filas || !ineUtil) return;
    let vivo = true;
    void contencionDe(clave, filas, ineUtil).then(contencion => { if (vivo) setMedida({ clave, contencion }); });
    return () => { vivo = false; };
  }, [clave, filas, ineUtil]);

  return useMemo(() => {
    if (!filas) return undefined;
    if (!terminado) return { estado: 'pendiente' };
    if (ineUtil && medida?.clave !== clave) return { estado: 'pendiente' };
    const informe = detectarIncidenciasDocumento(filas, {
      ineCode: ineUtil,
      contencion: ineUtil ? medida?.contencion : undefined,
    });
    const resumen = resumirIncidencias(informe);
    // INFDOC-2b (DOP-6 a): solo hay ventana si hay alguna fila con incidencias
    if (resumen.estado !== 'lista' || resumen.filas === 0) return resumen;
    const descargarInforme = documento
      ? () => descargarInformeAyuntamiento(informe, filas, {
          fileName: documento.fileName,
          municipio: documento.municipio,
          provincia: documento.provincia,
          ine: ineUtil ?? '',
          ...(documento.huella ? { huella: documento.huella } : {}),
        })
      : undefined;
    // INFDOC-3c2 (DOP-2 a): con la misma condición que el PDF
    const descargarCsv = documento
      ? () => descargarCsvIncidenciasGrea(informe, filas, { municipio: documento.municipio, ine: ineUtil ?? '' })
      : undefined;
    return {
      ...resumen,
      detalle: detallarIncidencias(informe, filas),
      ...(descargarInforme ? { descargarInforme } : {}),
      ...(descargarCsv ? { descargarCsv } : {}),
    };
  }, [filas, terminado, ineUtil, clave, medida, documento]);
}

// ============================================================================
// STYLES
// ============================================================================

const STYLES = {
  content: {
    padding: '20px 24px',
    paddingBottom: '16px',  // F4.04: FooterBar fijo ocupa ~64px; el stepper flotante añade margen
    margin: '0 56px',
    fontFamily: typography.fontFamily.sans,
  } as CSSProperties,

  // Estado vacío - simplificado, solo dropzone centrado
  emptyState: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    minHeight: '400px',
  } as CSSProperties,

  dropzoneWrapper: {
    width: '100%',
    maxWidth: '600px',
  } as CSSProperties,

  // P1-S2 (esc. 3): tarjeta de diagnóstico cero-infra (sin glow, DS PTEL)
  zeroInfraCard: {
    width: '100%',
    maxWidth: '600px',
    padding: '28px 32px',
    background: 'rgba(30,42,38,0.55)',
    border: '1px solid #3f3f3c',
    borderRadius: 12,
    textAlign: 'left' as const,
  } as CSSProperties,

  zeroInfraTitle: {
    margin: '0 0 12px',
    fontSize: 18,
    fontWeight: 600,
    color: '#e4e4e7',
    fontFamily: "'Space Grotesk', sans-serif",
  } as CSSProperties,

  zeroInfraText: {
    margin: '0 0 16px',
    fontSize: 13,
    lineHeight: 1.6,
    color: '#a1a1aa',
    fontFamily: "'Space Grotesk', sans-serif",
  } as CSSProperties,

  zeroInfraMetaRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    marginBottom: 6,
    fontSize: 12,
    color: '#a1a1aa',
    fontFamily: "'Space Grotesk', sans-serif",
  } as CSSProperties,

  zeroInfraMetaValue: {
    color: '#2DD4BF',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 12,
  } as CSSProperties,

  zeroInfraActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: 20,
  } as CSSProperties,

  zeroInfraPrimaryBtn: {
    padding: '9px 18px',
    background: 'rgba(45,212,191,0.12)',
    border: '1px solid rgba(45,212,191,0.4)',
    borderRadius: 8,
    color: '#2DD4BF',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Space Grotesk', sans-serif",
  } as CSSProperties,

  zeroInfraSecondaryBtn: {
    padding: '9px 18px',
    background: 'transparent',
    border: '1px solid #3f3f3c',
    borderRadius: 8,
    color: '#71717a',
    fontSize: 13,
    cursor: 'not-allowed',
    fontFamily: "'Space Grotesk', sans-serif",
  } as CSSProperties,

  // P1-S3 (esc. 4): confirmador de municipio (reutiliza tarjeta/tipografía 0-infra)
  muniSecondaryActiveBtn: {
    padding: '9px 18px',
    background: 'transparent',
    border: '1px solid #3f3f3c',
    borderRadius: 8,
    color: '#a1a1aa',
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: "'Space Grotesk', sans-serif",
  } as CSSProperties,

  muniCandidatoBtn: (selected: boolean): CSSProperties => ({
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    width: '100%',
    padding: '8px 12px',
    marginBottom: 6,
    background: selected ? 'rgba(45,212,191,0.12)' : 'transparent',
    border: selected ? '1px solid rgba(45,212,191,0.4)' : '1px solid #3f3f3c',
    borderRadius: 8,
    color: selected ? '#2DD4BF' : '#e4e4e7',
    fontSize: 13,
    cursor: 'pointer',
    textAlign: 'left' as const,
    fontFamily: "'Space Grotesk', sans-serif",
  }),

  muniCandidatoIne: {
    marginLeft: 'auto',
    color: '#71717a',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 12,
  } as CSSProperties,

  muniSearchInput: {
    width: '100%',
    padding: '9px 12px',
    background: 'rgba(9,12,11,0.6)',
    border: '1px solid #3f3f3c',
    borderRadius: 8,
    color: '#e4e4e7',
    fontSize: 13,
    outline: 'none',
    fontFamily: "'Space Grotesk', sans-serif",
  } as CSSProperties,

  muniPermissionNotice: {
    margin: '12px 0 0',
    fontSize: 12,
    color: '#fbbf24',
    fontFamily: "'Space Grotesk', sans-serif",
  } as CSSProperties,

  // Header con dropzone compacto
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  } as CSSProperties,

  filtersRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
    flexWrap: 'wrap' as const,
    gap: spacing[3],
  } as CSSProperties,

  filtersLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[2],
  } as CSSProperties,

  filtersRight: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[2],
  } as CSSProperties,
};

// ============================================================================
// HELPERS
// ============================================================================

function countByScoreRange(data: NormalizationRow[]): Record<FilterType, number> {
  const counts: Record<FilterType, number> = {
    all: data.length,
    high: 0,
    medium: 0,
    low: 0,
    critical: 0,
  };

  data.forEach(row => {
    // P1PROV-b (DOP-3 a): la fila aún en geolocalización no tiene nota final;
    // solo cuenta en «Todos».
    if (row.pendiente) return;
    const score = row.newScore;
    if (score == null) {
      counts.critical++; // Sin score = crítico
      return;
    }
    if (score >= 90) counts.high++;
    else if (score >= 70) counts.medium++;
    else if (score >= 50) counts.low++;
    else counts.critical++;
  });

  return counts;
}

function filterByScore(data: NormalizationRow[], filter: FilterType): NormalizationRow[] {
  if (filter === 'all') return data;
  
  return data.filter(row => {
    // P1PROV-b (DOP-3 a): la pendiente sale de los filtros por nota hasta que termine
    if (row.pendiente) return false;
    const score = row.newScore;
    if (score == null) return false;
    
    switch (filter) {
      case 'high': return score >= 90;
      case 'medium': return score >= 70 && score < 90;
      case 'low': return score >= 50 && score < 70;
      case 'critical': return score < 50;
      default: return true;
    }
  });
}

function filterByTypology(data: NormalizationRow[], filter: TypologyFilter): NormalizationRow[] {
  if (filter === 'all') return data;
  return data.filter(row => row.type === filter);
}

function filterBySearch(data: NormalizationRow[], query: string): NormalizationRow[] {
  if (!query.trim()) return data;
  const q = query.toLowerCase();
  return data.filter(row => 
    row.name.toLowerCase().includes(q) || 
    row.address?.toLowerCase().includes(q)
  );
}

// ============================================================================
// COMPONENT
// ============================================================================

export const Step1Page: FC<Step1PageProps> = ({
  documentContext,
  data,
  geolocatedCount,
  totalCount,
  isDemo = false,
  isProcessing = false,
  processingStatus = '',
  processingError = null,
  zeroInfraDiagnostic = null,
  municipioPendiente = null,
  canConfirmMunicipio = false,
  searchMunicipios,
  onConfirmMunicipio,
  isNormalizing = false,
  normalizationProgress,
  isGeocoding = false,
  geoProgress,
  onFileSelect,
  onFileReset,
  onPinClick,
  onCopyCoords,
  onContinue,
  filasFinales,
  ineDocumento,
  p1Terminado = false,
}) => {
  // INFDOC-2a: antes de cualquier `return` temprano (reglas de los hooks)
  const incidencias = useIncidenciasDocumento(filasFinales, ineDocumento, p1Terminado, documentContext);

  // State
  const [bannerVisible, setBannerVisible] = useState(true);
  const [demoBannerVisible, setDemoBannerVisible] = useState(true); // F4.03
  const [searchQuery, setSearchQuery] = useState('');
  const [scoreFilter, setScoreFilter] = useState<FilterType>('all');
  const [typeFilter, setTypeFilter] = useState<TypologyFilter>('all');

  // P1-S3 (esc. 4): estado local del confirmador de municipio
  const [muniSeleccionado, setMuniSeleccionado] = useState<MunicipioCandidato | null>(null);
  const [muniQuery, setMuniQuery] = useState('');
  const muniResultados: MunicipioCandidato[] =
    muniQuery.trim().length >= 2 && searchMunicipios ? searchMunicipios(muniQuery) : [];

  const [pageSize, setPageSize] = useState<number | 'all'>('all');

  // Determinar si hay datos cargados
  const hasData = documentContext !== null && data.length > 0;

  // ═══════════════════════════════════════════════════════════════════════════
  // ESTADO VACÍO: Sin archivo cargado - SOLO DROPZONE
  // ═══════════════════════════════════════════════════════════════════════════
  if (!hasData) {
    // ─── P1-S3 (esc. 4): municipio no detectado o ambiguo — GATE bloqueante ─
    // El INE condiciona la cascada de geolocalización y la validación:
    // sin municipio confirmado NO se procesa (decisión 4a). Vías de 4b:
    // (i) desplegable de candidatos con provincia si hay homónimos,
    // (ii) buscador de los 785 si no se detectó nada. Solo con permiso;
    // el catálogo INE maestro es inmutable.
    if (municipioPendiente) {
      const { fileName, municipioDetectado, candidatos } = municipioPendiente;
      const confirmDisabled = !muniSeleccionado || !canConfirmMunicipio;
      return (
        <div style={STYLES.emptyState}>
          <div style={STYLES.zeroInfraCard} role="alert" data-testid="municipio-gate">
            <h2 style={STYLES.zeroInfraTitle}>
              Confirma el municipio para continuar
            </h2>
            <p style={STYLES.zeroInfraText}>
              El código INE del municipio condiciona la geolocalización y la
              validación de las coordenadas, así que el procesamiento queda
              bloqueado hasta confirmarlo.
            </p>
            <div style={STYLES.zeroInfraMetaRow}>
              <span>Archivo:</span>
              <span style={STYLES.zeroInfraMetaValue}>{fileName}</span>
            </div>
            <div style={STYLES.zeroInfraMetaRow}>
              <span>Municipio detectado:</span>
              {municipioDetectado ? (
                <span style={STYLES.zeroInfraMetaValue}>
                  {municipioDetectado} — sin correspondencia INE en Andalucía
                </span>
              ) : (
                <span>no detectado</span>
              )}
            </div>

            {candidatos.length > 0 && (
              <div style={{ margin: '16px 0 4px' }}>
                <p style={{ ...STYLES.zeroInfraText, margin: '0 0 8px' }}>
                  ¿Se trata de alguno de estos municipios?
                </p>
                {candidatos.map(c => (
                  <button
                    key={c.codigo}
                    type="button"
                    style={STYLES.muniCandidatoBtn(muniSeleccionado?.codigo === c.codigo)}
                    data-testid={`municipio-candidato-${c.codigo}`}
                    onClick={() => setMuniSeleccionado(c)}
                  >
                    <span>{c.nombre} ({c.provincia})</span>
                    <span style={STYLES.muniCandidatoIne}>INE {c.codigo}</span>
                  </button>
                ))}
              </div>
            )}

            <div style={{ margin: '16px 0 4px' }}>
              <input
                type="text"
                style={STYLES.muniSearchInput}
                placeholder="Buscar entre los 785 municipios de Andalucía…"
                value={muniQuery}
                data-testid="municipio-buscador"
                onChange={e => setMuniQuery(e.target.value)}
              />
              {muniResultados.map(c => (
                <button
                  key={`busq-${c.codigo}`}
                  type="button"
                  style={{ ...STYLES.muniCandidatoBtn(muniSeleccionado?.codigo === c.codigo), marginTop: 6 }}
                  data-testid={`municipio-resultado-${c.codigo}`}
                  onClick={() => setMuniSeleccionado(c)}
                >
                  <span>{c.nombre} ({c.provincia})</span>
                  <span style={STYLES.muniCandidatoIne}>INE {c.codigo}</span>
                </button>
              ))}
            </div>

            {!canConfirmMunicipio && (
              <p style={STYLES.muniPermissionNotice} data-testid="municipio-sin-permiso">
                Necesitas permiso de edición para confirmar el municipio.
                Solicítalo desde tu perfil o contacta con el GREA.
              </p>
            )}

            <div style={STYLES.zeroInfraActions}>
              <button
                type="button"
                style={confirmDisabled ? STYLES.zeroInfraSecondaryBtn : STYLES.zeroInfraPrimaryBtn}
                disabled={confirmDisabled}
                data-testid="municipio-confirmar"
                title={
                  !canConfirmMunicipio
                    ? 'Necesitas permiso de edición'
                    : !muniSeleccionado
                      ? 'Selecciona primero un municipio'
                      : `Confirmar ${muniSeleccionado.nombre} (INE ${muniSeleccionado.codigo})`
                }
                onClick={() => {
                  if (!confirmDisabled && muniSeleccionado) {
                    onConfirmMunicipio?.(muniSeleccionado.codigo);
                  }
                }}
              >
                Confirmar municipio
              </button>
              <button
                type="button"
                style={STYLES.muniSecondaryActiveBtn}
                onClick={() => onFileReset?.()}
              >
                Cargar otro documento
              </button>
            </div>
          </div>
        </div>
      );
    }

    // ─── P1-S2 (esc. 3): documento válido con 0 infraestructuras ───────────
    // Diagnóstico accionable en lugar de error mudo. El preview de filas
    // crudas queda diferido a S2-b; el mapeo manual de columnas es el
    // mini-flujo MAPEO-COLUMNAS-P1 (chat dedicado, aquí solo el enganche).
    if (zeroInfraDiagnostic) {
      const { fileName, fileType, municipio, provincia, ine } = zeroInfraDiagnostic;
      return (
        <div style={STYLES.emptyState}>
          <div style={STYLES.zeroInfraCard} role="alert">
            <h2 style={STYLES.zeroInfraTitle}>
              No se han reconocido infraestructuras en el documento
            </h2>
            <p style={STYLES.zeroInfraText}>
              El documento se ha leído correctamente, pero ninguna de sus tablas
              contenía infraestructuras reconocibles. Suele deberse a encabezados
              de columna atípicos que la detección automática no reconoce.
            </p>
            <div style={STYLES.zeroInfraMetaRow}>
              <span>Archivo:</span>
              <span style={STYLES.zeroInfraMetaValue}>{fileName}</span>
              <span>(formato {fileType.toUpperCase()})</span>
            </div>
            <div style={STYLES.zeroInfraMetaRow}>
              <span>Municipio detectado:</span>
              {municipio ? (
                <span style={STYLES.zeroInfraMetaValue}>
                  {municipio}
                  {provincia ? ` (${provincia})` : ''}
                  {ine ? ` · INE ${ine}` : ''}
                </span>
              ) : (
                <span>no detectado</span>
              )}
            </div>
            <div style={STYLES.zeroInfraActions}>
              <button
                type="button"
                style={STYLES.zeroInfraPrimaryBtn}
                onClick={() => onFileReset?.()}
              >
                Cargar otro documento
              </button>
              <button
                type="button"
                style={STYLES.zeroInfraSecondaryBtn}
                disabled
                title="Disponible próximamente"
              >
                Mapear columnas manualmente
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={STYLES.emptyState}>
        <div style={STYLES.dropzoneWrapper}>
          <FileDropzone
            variant="prominent"
            isProcessing={isProcessing}
            processingStatus={processingStatus}
            error={processingError}
            onFileSelect={onFileSelect || (() => {})}
          />
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ESTADO CON DATOS: Archivo cargado
  // ═══════════════════════════════════════════════════════════════════════════

  // Filtered data pipeline
  let filteredData = filterBySearch(data, searchQuery);
  filteredData = filterByScore(filteredData, scoreFilter);
  filteredData = filterByTypology(filteredData, typeFilter);

  // Pagination
  const paginatedData = pageSize === 'all' 
    ? filteredData 
    : filteredData.slice(0, pageSize);

  // Score counts (from original data, not filtered)
  const scoreCounts = countByScoreRange(data);

  // P1-S4 (§B): desglose por vía de resolución (solo si el pipeline corrió;
  // las filas sin procedencia —heurística pre-pipeline— no cuentan).
  const viaBreakdown = data.reduce<ViaBreakdown>((acc, row) => {
    if (row.procedencia) acc[row.procedencia] += 1;
    return acc;
  }, emptyViaBreakdown());

  // Handlers
  const handlePinClick = (row: NormalizationRow) => {
    onPinClick?.(row);
    toast.success('Marcador fijado', {
      description: row.name,
      duration: 2500,
    });
  };

  const handleCopyCoords = (row: NormalizationRow) => {
    onCopyCoords?.(row);
    toast.success('Coordenadas copiadas', {
      description: row.name,
      duration: 2000,
    });
  };

  return (
    <>
      {/* Demo Banner — F4.03: patrón único dismissable, alineado con content (0 56px) */}
      {isDemo && (
        <div style={{ margin: '0 56px' }}>
          <InfoBanner
            variant="info"
            visible={demoBannerVisible}
            onClose={() => setDemoBannerVisible(false)}
          >
            <BannerStrong>Modo demo</BannerStrong>{' '}
            — Datos de ejemplo de Colomera, Granada (18051). Carga tu propio documento PTEL para procesar datos reales.
          </InfoBanner>
        </div>
      )}

      {/* Info Banner — B15 (podado en P1BR-S2, DOP-P1BR-BANNER = c restringido):
          las variantes «completada» y «parcial» desaparecen — su información
          vive ahora en DocumentStatusModule. Solo sobrevive el error. */}
      <div style={{ margin: '0 56px' }}>
        {/* B15: 0 normalizaciones — error de formato */}
        {geolocatedCount === 0 && totalCount > 0 && (
          <InfoBanner
            variant="error"
            visible={bannerVisible}
            onClose={() => setBannerVisible(false)}
          >
            <BannerStrong>No se han podido normalizar coordenadas automáticamente.</BannerStrong>{' '}
            Verifica que el documento contiene columnas X/Y o coordenadas UTM/WGS84 válidas.
          </InfoBanner>
        )}
      </div>

      {/* ContextBar DS v2 retirada del montaje en P1BR-S2 (Opción A,
          DOP-P1BR-ESTRUCTURA). Desde FILAPASOS el contexto del documento y
          «Cancelar documento» son la primera línea de DocumentStatusModule
          (DocumentStrip); la fila superior solo lleva los pasos. El
          componente ContextBar sigue vivo — lo consume Step2Page. */}

      {/* Main Content */}
      <main style={STYLES.content}>
        {/* RESIL-M1: Banner de salud de APIs — solo visible si una API crítica está caída */}
        <ApiHealthBanner />

        {/* P1UNO: un solo panel de estado y proceso. Sustituye a ProgressBar
            (modos 'processing'/'geocoding', que se montaban encima durante el
            pipeline) y al módulo de estado: tres fases, misma geometría. */}
        <DocumentStatusModule
          data={data}
          viaBreakdown={viaBreakdown}
          fase={isGeocoding ? 'geolocalizando' : isNormalizing ? 'normalizando' : 'terminado'}
          progress={isGeocoding ? geoProgress : isNormalizing ? normalizationProgress : undefined}
          // FILAPASOS (C′): identidad del documento + «Cancelar documento» como
          // primera línea del panel (salieron de la fila de pasos).
          documento={
            documentContext
              ? {
                  fileName: documentContext.fileName,
                  municipality: documentContext.municipio,
                  province: documentContext.provincia,
                  ineCode: documentContext.ine,
                }
              : null
          }
          onCancelarDocumento={onFileReset}
          incidencias={incidencias}
        />

        {/* Filters Row */}
        <div style={STYLES.filtersRow}>
          <div style={STYLES.filtersLeft}>
            <SearchBox
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Buscar infraestructura..."
            />
          </div>
          <div style={STYLES.filtersRight}>
            <FilterPills
              counts={scoreCounts}
              selected={scoreFilter}
              onSelect={setScoreFilter}
            />
          </div>
        </div>

        {/* Table */}
        <NormalizationTable
          data={paginatedData}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
          onPinClick={handlePinClick}
          onCopyCoords={handleCopyCoords}
        />

        {/* Pagination */}
        <Pagination
          pageSize={pageSize}
          totalItems={filteredData.length}
          visibleItems={paginatedData.length}
          onPageSizeChange={setPageSize}
        />
      </main>

      {/* FooterBar — F4.04: «Continuar a Valida» */}
      {onContinue && (
        <FooterBar
          summary={
            <>
              <span>{geolocatedCount} geolocalizados</span>
              {scoreCounts.critical > 0 && (
                <span style={{ color: '#F87171' }}>· {scoreCounts.critical} críticos</span>
              )}
            </>
          }
          continueLabel="Continuar a Valida →"
          onContinue={onContinue}
          continueDisabled={totalCount === 0}
        />
      )}
    </>
  );
};

export default Step1Page;
